/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type CapModuleSpecsFragment } from '../../../../api/fragments/CapModuleSpecs';
import {
    UpdateCapModuleDocument,
    type UpdateCapModuleMutation,
    type UpdateCapModuleMutationVariables,
} from '../../../../api/mutations/updateCapModule';
import { type CapModuleSettings } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import CapModuleMainDetailsForm from './CapModuleDetailsForm';

export type FormValues = CapModuleSettings;

const CapModuleMainDetails = ({ module }: { module: CapModuleSpecsFragment }) => {
    const { t } = useTranslation(['moduleDetails']);

    const initialValues = useMemo(
        (): CapModuleSettings => ({
            ...pick(['displayName', 'capSetting'], module),
        }),
        [module]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('capModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<UpdateCapModuleMutation, UpdateCapModuleMutationVariables>({
                    mutation: UpdateCapModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: { ...values },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('capModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <CapModuleMainDetailsForm />
                </Form>
            )}
        </Formik>
    );
};

export default CapModuleMainDetails;
